import { useState, useRef, useEffect, useCallback } from "react";
import { ReservationFormData, AddressSuggestion } from "../types";
import { POINTS_INTERET } from "../constants";
import { searchAddresses as apiSearchAddresses } from "@/lib/api";

/**
 * Gère l'autocomplétion d'adresses (départ / arrivée / étapes) :
 * - debounce par champ (300 ms) avec nettoyage au démontage ;
 * - garde anti-réponse-obsolète via un identifiant de requête par champ
 *   (une réponse lente « can » ne doit pas écraser une réponse récente
 *   « cannes ») ;
 * - filtres rapides par type de POI (gare / aéroport / destination / hôtel).
 *
 * Extrait tel quel de `useReservationForm` (aucun changement de comportement).
 * `setFormData` et `setEstimatedPrice` sont fournis par le hook parent afin de
 * préserver exactement les effets de bord d'origine (mise à jour des champs +
 * reset du prix à la sélection).
 */
export const useAddressAutocomplete = (
  formData: ReservationFormData,
  setFormData: React.Dispatch<React.SetStateAction<ReservationFormData>>,
  setEstimatedPrice: React.Dispatch<React.SetStateAction<number | null>>,
) => {
  // États pour les suggestions d'adresses
  const [departureSuggestions, setDepartureSuggestions] = useState<
    AddressSuggestion[]
  >([]);
  const [arrivalSuggestions, setArrivalSuggestions] = useState<
    AddressSuggestion[]
  >([]);
  const [stopSuggestions, setStopSuggestions] = useState<
    Record<string, AddressSuggestion[]>
  >({});
  const [showDepartureSuggestions, setShowDepartureSuggestions] =
    useState(false);
  const [showArrivalSuggestions, setShowArrivalSuggestions] = useState(false);
  const [showStopSuggestions, setShowStopSuggestions] = useState<
    Record<string, boolean>
  >({});
  const [isLoadingDeparture, setIsLoadingDeparture] = useState(false);
  const [isLoadingArrival, setIsLoadingArrival] = useState(false);
  const [isLoadingStop, setIsLoadingStop] = useState<Record<string, boolean>>(
    {},
  );

  // Refs pour les timeouts de debounce
  const departureTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const arrivalTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const stopTimeoutRefs = useRef<Record<string, NodeJS.Timeout | null>>({});

  // Identifiants de requête par champ : on n'applique une réponse que si elle
  // correspond à la DERNIÈRE requête lancée pour ce champ. Évite qu'une réponse
  // lente (« can ») n'écrase une réponse plus récente (« cannes »).
  const departureReqIdRef = useRef(0);
  const arrivalReqIdRef = useRef(0);
  const stopReqIdRefs = useRef<Record<string, number>>({});

  // Nettoyage des timeouts de debounce au démontage (évite les fuites et les
  // setState après démontage).
  useEffect(() => {
    // Copie locale des refs : leur .current peut avoir changé au moment où le
    // nettoyage s'exécute (recommandation react-hooks/exhaustive-deps).
    const departureTimeout = departureTimeoutRef;
    const arrivalTimeout = arrivalTimeoutRef;
    const stopTimeouts = stopTimeoutRefs;
    return () => {
      if (departureTimeout.current) {
        clearTimeout(departureTimeout.current);
      }
      if (arrivalTimeout.current) {
        clearTimeout(arrivalTimeout.current);
      }
      Object.values(stopTimeouts.current).forEach((timeoutId) => {
        if (timeoutId) clearTimeout(timeoutId);
      });
    };
  }, []);

  const fetchSuggestions = async (
    query: string,
    type: "departure" | "arrival" | "stop",
    stopId?: string,
  ) => {
    if (query.length < 3) {
      if (type === "departure") {
        setDepartureSuggestions([]);
        setShowDepartureSuggestions(false);
      } else if (type === "arrival") {
        setArrivalSuggestions([]);
        setShowArrivalSuggestions(false);
      } else if (type === "stop" && stopId) {
        setStopSuggestions((prev) => ({ ...prev, [stopId]: [] }));
        setShowStopSuggestions((prev) => ({ ...prev, [stopId]: false }));
      }
      return;
    }

    // Identifiant de cette requête : on ne tiendra compte de la réponse que si
    // c'est toujours la plus récente pour ce champ au moment où elle arrive.
    let requestId = 0;
    if (type === "departure") {
      requestId = ++departureReqIdRef.current;
    } else if (type === "arrival") {
      requestId = ++arrivalReqIdRef.current;
    } else if (type === "stop" && stopId) {
      requestId = (stopReqIdRefs.current[stopId] ?? 0) + 1;
      stopReqIdRefs.current[stopId] = requestId;
    }

    const isStale = () => {
      if (type === "departure") return requestId !== departureReqIdRef.current;
      if (type === "arrival") return requestId !== arrivalReqIdRef.current;
      if (type === "stop" && stopId)
        return requestId !== stopReqIdRefs.current[stopId];
      return false;
    };

    try {
      if (type === "departure") {
        setIsLoadingDeparture(true);
      } else if (type === "arrival") {
        setIsLoadingArrival(true);
      } else if (type === "stop" && stopId) {
        setIsLoadingStop((prev) => ({ ...prev, [stopId]: true }));
      }

      const suggestions = await apiSearchAddresses(query);

      // Réponse obsolète (une requête plus récente a été lancée) : on l'ignore.
      if (isStale()) return;

      if (type === "departure") {
        setDepartureSuggestions(suggestions);
        setShowDepartureSuggestions(true);
        setIsLoadingDeparture(false);
      } else if (type === "arrival") {
        setArrivalSuggestions(suggestions);
        setShowArrivalSuggestions(true);
        setIsLoadingArrival(false);
      } else if (type === "stop" && stopId) {
        setStopSuggestions((prev) => ({ ...prev, [stopId]: suggestions }));
        setShowStopSuggestions((prev) => ({ ...prev, [stopId]: true }));
        setIsLoadingStop((prev) => ({ ...prev, [stopId]: false }));
      }
    } catch (error) {
      console.error("Erreur lors de la recherche d'adresses:", error);
      if (isStale()) return;
      if (type === "departure") {
        setIsLoadingDeparture(false);
      } else if (type === "arrival") {
        setIsLoadingArrival(false);
      } else if (type === "stop" && stopId) {
        setIsLoadingStop((prev) => ({ ...prev, [stopId]: false }));
      }
    }
  };

  const handleAddressInput = (
    e: React.ChangeEvent<HTMLInputElement>,
    type: "departure" | "arrival" | "returnArrival" | "stop",
    stopId?: string,
  ) => {
    const { value } = e.target;

    if (type === "departure") {
      setFormData((prev) => ({ ...prev, departure: value }));
      if (departureTimeoutRef.current) {
        clearTimeout(departureTimeoutRef.current);
      }
      const timeoutId = setTimeout(() => {
        fetchSuggestions(value, "departure");
      }, 300);
      departureTimeoutRef.current = timeoutId;
    } else if (type === "arrival" || type === "returnArrival") {
      setFormData((prev) => ({
        ...prev,
        [type]: value,
      }));
      if (arrivalTimeoutRef.current) {
        clearTimeout(arrivalTimeoutRef.current);
      }
      const timeoutId = setTimeout(() => {
        fetchSuggestions(value, "arrival");
      }, 300);
      arrivalTimeoutRef.current = timeoutId;
    } else if (type === "stop" && stopId) {
      const updatedStops = formData.stops.map((stop) =>
        stop.id === stopId ? { ...stop, address: value } : stop,
      );
      setFormData((prev) => ({ ...prev, stops: updatedStops }));
      if (stopTimeoutRefs.current[stopId]) {
        clearTimeout(stopTimeoutRefs.current[stopId]);
      }
      const timeoutId = setTimeout(() => {
        fetchSuggestions(value, "stop", stopId);
      }, 300);
      stopTimeoutRefs.current[stopId] = timeoutId;
    }
  };

  const selectSuggestion = (
    suggestion: AddressSuggestion,
    type: "departure" | "arrival" | "returnArrival" | "stop",
    stopId?: string,
  ) => {
    const selectedPoiType: ReservationFormData["selectedDepartureType"] =
      suggestion.type === "aeroport" ||
      suggestion.type === "gare" ||
      suggestion.type === "destination" ||
      suggestion.type === "hotel"
        ? (suggestion.type as ReservationFormData["selectedDepartureType"])
        : null;

    if (type === "departure") {
      setFormData((prev) => ({
        ...prev,
        departure: suggestion.label,
        selectedDepartureType: selectedPoiType,
        departureFlightNumber: prev.departureFlightNumber || "",
        departureTrainNumber: prev.departureTrainNumber || "",
      }));
      setShowDepartureSuggestions(false);
    } else if (type === "arrival" || type === "returnArrival") {
      setFormData((prev) => ({
        ...prev,
        [type]: suggestion.label,
        selectedArrivalType: selectedPoiType,
        arrivalFlightNumber: prev.arrivalFlightNumber || "",
        arrivalTrainNumber: prev.arrivalTrainNumber || "",
      }));
      setShowArrivalSuggestions(false);
    } else if (type === "stop" && stopId) {
      const updatedStops = formData.stops.map((stop) =>
        stop.id === stopId ? { ...stop, address: suggestion.label } : stop,
      );
      setFormData((prev) => ({ ...prev, stops: updatedStops }));
      setShowStopSuggestions((prev) => ({ ...prev, [stopId]: false }));
    }

    // Réinitialiser le cache des prix
    setEstimatedPrice(null);
  };

  const filterByPOIType = useCallback(
    async (
      type: "gare" | "aeroport" | "destination" | "hotel",
      context: "departure" | "arrival" | "stop",
      stopId?: string,
    ) => {
      // Réinitialiser les suggestions existantes
      if (context === "departure") {
        setDepartureSuggestions([]);
        setShowDepartureSuggestions(false);
      } else if (context === "arrival") {
        setArrivalSuggestions([]);
        setShowArrivalSuggestions(false);
      } else if (context === "stop" && stopId) {
        setStopSuggestions((prev) => ({ ...prev, [stopId]: [] }));
        setShowStopSuggestions((prev) => ({
          ...prev,
          [stopId]: false,
        }));
      }

      // Attendre un court instant pour laisser l'UI se mettre à jour
      await new Promise((resolve) => setTimeout(resolve, 100));

      const points =
        type === "gare"
          ? POINTS_INTERET.gares
          : type === "aeroport"
            ? POINTS_INTERET.aeroports
            : type === "destination"
              ? POINTS_INTERET.destinations
              : POINTS_INTERET.hotels;

      const sortedPoints =
        type === "hotel"
          ? [...points].sort((a, b) => {
              const getHotelPriority = (point: (typeof points)[number]) => {
                const haystack = `${point.nom} ${point.adresse}`.toLowerCase();
                if (haystack.includes("cannes")) return 0;
                if (
                  haystack.includes("monaco") ||
                  haystack.includes("monte carlo") ||
                  haystack.includes("monte-carlo")
                ) {
                  return 1;
                }
                return 2;
              };

              const priorityDiff = getHotelPriority(a) - getHotelPriority(b);
              if (priorityDiff !== 0) return priorityDiff;

              return a.nom.localeCompare(b.nom, "fr", {
                sensitivity: "base",
              });
            })
          : points;

      const suggestions: AddressSuggestion[] = sortedPoints.map((point) => ({
        id: point.id,
        label: point.adresse,
        name: point.nom,
        postcode: point.adresse.split(" ").pop() || "",
        city: point.adresse.split(" ").slice(-2, -1).join(" "),
        type: point.type,
        context:
          point.type === "gare"
            ? "Gare"
            : point.type === "aeroport"
              ? "Aéroport"
              : point.type === "hotel"
                ? "Hôtel"
                : "Destination",
        isPOI: true,
        icon: point.icon,
        coordinates: point.coordinates,
        score: 1,
      }));

      // Mettre à jour les suggestions
      if (context === "departure") {
        setDepartureSuggestions(suggestions);
        setShowDepartureSuggestions(true);
      } else if (context === "arrival") {
        setArrivalSuggestions(suggestions);
        setShowArrivalSuggestions(true);
      } else if (context === "stop" && stopId) {
        setStopSuggestions((prev) => ({ ...prev, [stopId]: suggestions }));
        setShowStopSuggestions((prev) => ({ ...prev, [stopId]: true }));
      }
    },
    [],
  );

  return {
    departureSuggestions,
    setDepartureSuggestions,
    arrivalSuggestions,
    setArrivalSuggestions,
    stopSuggestions,
    setStopSuggestions,
    showDepartureSuggestions,
    setShowDepartureSuggestions,
    showArrivalSuggestions,
    setShowArrivalSuggestions,
    showStopSuggestions,
    setShowStopSuggestions,
    isLoadingDeparture,
    setIsLoadingDeparture,
    isLoadingArrival,
    setIsLoadingArrival,
    isLoadingStop,
    setIsLoadingStop,
    fetchSuggestions,
    handleAddressInput,
    selectSuggestion,
    filterByPOIType,
  };
};
